import React, { useState, useEffect } from "react";
import { Box, Typography, Avatar, IconButton, Grid, Paper, Button } from "@mui/material";
import { ArrowBack, ArrowForward, Phone, PhoneAndroid } from "@mui/icons-material";
import useStore, { useStoreActions } from "../../../store.js";

const doctor = {
  name: "Dr. John Doe, MD",
  specialty: "General Physician",
  address: "123 Main Street, New York, NY 10001",
  phone: "123-456-7890",
  image: "https://via.placeholder.com/80", // Dummy profile image
};

const groupByDate = (data) => {
  // Generate the next 5 days if data is missing or empty
  if (!data || data.length === 0) {
    let dates = [];
    let formatter = new Intl.DateTimeFormat("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });

    for (let i = 0; i < 5; i++) {
      let nextDate = new Date();
      nextDate.setDate(nextDate.getDate() + i); // Get the next i-th day
      let formattedDate = formatter.format(nextDate); // Format as MM/DD/YYYY
      dates.push({ date: formattedDate, time: [] });
    }

    return dates.reduce((acc, curr) => {
      acc[curr.date] = curr.time;
      return acc;
    }, {});
  }

  // Group existing data by date
  return data.reduce((acc, curr) => {
    if (!acc[curr.date]) acc[curr.date] = [];
    acc[curr.date].push({
      time: curr.starttime || "",
      id: curr.appointmentid || "",
      appointmenttypeid: curr.appointmenttypeid || "",
    });
    return acc;
  }, {});
};

// Validation function to ensure appointment type IDs are correct
const validateSlotAppointmentTypes = (slots) => {
  if (!slots || !Array.isArray(slots)) {
    console.warn('Invalid slots array for validation');
    return [];
  }

  const validatedSlots = slots.filter(slot => {
    const hasValidId = slot.appointmenttypeid && !isNaN(slot.appointmenttypeid);
    if (!hasValidId) {
      console.warn('Slot missing valid appointment type ID:', slot);
    }
    return hasValidId;
  });

  console.log(`Validated ${validatedSlots.length}/${slots.length} slots with valid appointment type IDs`);
  
  // Log appointment type ID distribution for debugging
  const appointmentTypeDistribution = validatedSlots.reduce((acc, slot) => {
    const key = `${slot.patientappointmenttypename || slot.appointmenttype} (ID: ${slot.appointmenttypeid})`;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  
  console.log('Appointment type distribution in validated slots:', appointmentTypeDistribution);
  
  return validatedSlots;
};

const AppointmentBooking = ({ availableSlot, provider, setScreen, loading }) => {
  const { setAppointmentDetails } = useStoreActions();
  const { appointmentDetails, appointmentTypes, selectedType } = useStore();
  
  // Validate and process slots to ensure they have correct appointment type IDs
  const processedSlots = validateSlotAppointmentTypes(availableSlot || []);
  const groupedData = groupByDate(processedSlots);
  const allDates = Object.keys(groupedData);
  console.log("🔒🔒🔒", groupedData);
  const [startIndex, setStartIndex] = useState(0);
  const visibleDates = allDates.slice(startIndex, startIndex + 4);
  const [providerImage, setProviderImage] = useState(null);
  
  // Function to dynamically load provider image
  const loadProviderImage = (provider) => {
    if (!provider) return null;
    
    // Reset the provider image first
    setProviderImage(null);
    
    // Get provider name and ID for debugging
    const providerId = provider.providerid || provider.id;
    const firstName = provider.firstname || '';
    const lastName = provider.lastname || '';
    
    console.log(`[${new Date().toISOString()}] Loading image for provider:`, providerId, firstName, lastName);
    
    // Convert provider name to lowercase and replace spaces with dashes for filename
    const providerName = `${firstName} ${lastName}`;
    const providerFileName = providerName.toLowerCase().replace(/\s+/g, '-');
    
    try {
      // Dynamic import with timestamp to prevent caching
      import(`../../../assets/images/providers/${providerFileName}.png`)
        .then(image => {
          console.log(`Image loaded for provider ${providerId}:`, providerFileName);
          setProviderImage(image.default);
        })
        .catch(err => {
          console.error(`Failed to load image for provider ${providerId}:`, err);
          setProviderImage(null);
        });
    } catch (error) {
      console.error(`Error in image import for provider ${providerId}:`, error);
      setProviderImage(null);
    }
  };
  
  // Effect to load provider image when provider changes
  useEffect(() => {
    loadProviderImage(provider);
  }, [provider?.providerid, provider?.id, provider?.firstname, provider?.lastname]); // Expanded dependencies to catch all changes

  const handleNext = () => {
    if (startIndex + 4 < allDates.length) {
      setStartIndex(startIndex + 1);
    }
  };

  const handlePrev = () => {
    if (startIndex > 0) {
      setStartIndex(startIndex - 1);
    }
  };

  const dateTimeHandler = (e) => {
    const time = e.currentTarget.dataset.time;
    const date = e.currentTarget.dataset.date;
    const id = e.currentTarget.dataset.appointmentid;
    const appointmenttypeid = e.currentTarget.dataset.appointmenttypeid;
    if (time && date && id) {
      setAppointmentDetails({ date, time, id, appointmenttypeid });
      setScreen(2);
    }
  };
  
  // Helper function to get appointment duration
  const getAppointmentDuration = (appointmenttypeid) => {
    const appointmentType = appointmentTypes.find(type => type.appointmenttypeid === appointmenttypeid || type.appointmenttypeid === selectedType);
    return appointmentType?.duration || 30; // Default to 30 minutes if not found
  };
  
  // Helper function to calculate end time
  const calculateEndTime = (startTime, date, duration) => {
    const startDateTime = new Date(`${date} ${startTime}`);
    const endDateTime = new Date(startDateTime.getTime() + duration * 60000);
    return endDateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  console.log("availableSlot :", availableSlot, "Provider : ", provider);

  return (
    <>
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-2 mt-4 lg:mt-10 h-full flex-grow">
        {/* Left Side Col */}
        <div className="flex flex-col lg:basis-1/2 gap-4 lg:gap-10">
          <Typography variant="h5" fontWeight="medium" color="primary" gutterBottom>
            CHOOSE A TIME
          </Typography>
          <div className="flex flex-col sm:flex-row gap-4 items-center sm:items-start">
            <Avatar 
              key={`provider-avatar-${provider?.providerid || provider?.id || Math.random()}`}
              src={providerImage || `/profile-${provider?.providerid || provider?.id || 'default'}.jpg`} 
              alt={provider?.firstname || provider?.displayname || 'Provider'} 
              sx={{ width: { xs: 80, sm: 100 }, height: { xs: 80, sm: 100 } }} 
            />
            <div className="text-center sm:text-left">
              <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                {provider.displayname || "Dr. " + provider.firstname + " " + provider.lastname}
              </Typography>
              {/* <Typography variant="body2" className="pt-3">
                {provider.specialty || provider.providertype}
              </Typography>
              <Typography variant="body2">{provider.ansinamecode}</Typography> */}
            </div>
          </div>
        </div>

        {/* Right Side Col */}
        <div className="flex-grow flex flex-col justify-between lg:basis-1/2">
          {/* Appointment Slot Display */}
          {availableSlot.length > 0 ? (
            // Date Navigation and Time Slots (only shown when slots are available)
            <Box display="flex" alignItems="flex-start" justifyContent="center" mb={2}>
              <IconButton onClick={handlePrev} disabled={startIndex === 0}>
                <ArrowBack />
              </IconButton>

              <Box 
                display="flex" 
                className="max-h-[280px] overflow-x-auto" 
                sx={{ 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 2, sm: 1 },
                  px: { xs: 1, sm: 0 }
                }}
              >
                {visibleDates.map((date, index) => (
                  <Box 
                    key={index} 
                    textAlign="center" 
                    minWidth={{ xs: '100%', sm: 70 }} 
                    borderRadius={2} 
                    display="flex" 
                    flexDirection="column" 
                    justifyContent="start"
                    sx={{ mb: { xs: 2, sm: 0 } }}
                  >
                    {/* Date Header */}
                    <Box sx={{ 
                      mb: { xs: 1, sm: 3 },
                      pb: { xs: 1, sm: 0 },
                      borderBottom: { xs: '1px solid #e0e0e0', sm: 'none' }
                    }}>
                      <Typography variant="caption" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
                        {new Date(date).toLocaleDateString("en-US", { weekday: "short" })}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '0.875rem' } }}>
                        {new Date(date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: { xs: 'row', sm: 'column' },
                      flexWrap: { xs: 'wrap', sm: 'nowrap' },
                      gap: { xs: 1, sm: 0 },
                      justifyContent: { xs: 'flex-start', sm: 'center' }
                    }}>
                      {/* Time Slots */}
                      {groupedData[date].map((appointment, i) => {
                        const duration = getAppointmentDuration(appointment.appointmenttypeid);
                        const endTime = calculateEndTime(appointment.time, date, duration);
                        
                        return (
                          <Button 
                            key={i} 
                            onClick={dateTimeHandler} 
                            data-time={appointment.time} 
                            data-date={date} 
                            data-appointmentid={appointment.id} 
                            data-appointmenttypeid={appointment.appointmenttypeid}
                            sx={{ 
                              minWidth: { xs: 'auto', sm: 120 },
                              width: { xs: 'auto', sm: '100%' },
                              display: 'flex',
                              flexDirection: 'column',
                              padding: { xs: '6px 12px', sm: '8px 16px' },
                              marginBottom: { xs: 0, sm: '8px' },
                              fontSize: { xs: '0.75rem', sm: '1rem' },
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {appointment.time}
                            </Typography>
                            {/* <Typography variant="caption" color="text.secondary">
                              ({duration} min, ends {endTime})
                            </Typography> */}
                          </Button>
                        );
                      })}
                    </Box>
                  </Box>
                ))}
              </Box>

              <IconButton onClick={handleNext} disabled={startIndex + 4 >= allDates.length}>
                <ArrowForward />
              </IconButton>
            </Box>
          ) : (
            // No slots available message and call-to-office section
            <Box sx={{ my: 4 }}>
              {!loading && (
                <Box mt={2} p={3} bgcolor="#ffebee" borderRadius={1} mb={4}>
                  {(provider?.providerid === 1 || provider?.providerid === "1" || provider?.id === 1 || provider?.id === "1") ? (
                    // Special message for Dr. Tovmassian (Provider ID 1)
                    <Box textAlign="center">
                      <Typography color="primary" variant="h6" gutterBottom>
                        Dr. Tovmassian is only available through concierge medicine.
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Please visit the link below to schedule your consultation:
                      </Typography>
                      <Button 
                        variant="contained" 
                        color="primary" 
                        href="https://book.elevatehealthgroup.com/concierge-medecine/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        sx={{ mt: 1, borderRadius: '25px' }}
                      >
                        Schedule Concierge Consultation
                      </Button>
                    </Box>
                  ) : (
                    // Default no slots message for other providers
                    <Typography color="error" textAlign={"center"} variant="h6">
                      No open slots available
                    </Typography>
                  )}
                </Box>
              )}
              
              {/* Info Section */}
              <Grid container direction="column" spacing={2} justifyContent="center" mt={3}>
                <div className="border-2 border-[#383352] inline-block max-w-min p-1 rounded-full mx-auto mb-3">
                  <PhoneAndroid sx={{ fontSize: 30, color: "#383352" }} />
                </div>
                <p className="text-center">
                  Please call our office at <strong> 818-246-8000 </strong> to schedule your <br /> appointment with {provider ? (provider.displayname || `Dr. ${provider.firstname || ''} ${provider.lastname || ''}`) : "your provider"}.
                </p>
              </Grid>
            </Box>
          )}
        </div>
      </div>
    </>
  );
};

export default AppointmentBooking;
